<script setup>
import { ref, onBeforeUnmount } from 'vue'

const textInput = ref('')
const morseOutput = ref('')
const wpm = ref(15)
const isPlaying = ref(false)

// 音频上下文
let audioContext = null
let oscillator = null
let gainNode = null

// 莫尔斯电码映射
const morseCodeMap = {
  'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.', 'G': '--.', 'H': '....', 'I': '..', 'J': '.---',
  'K': '-.-', 'L': '.-..', 'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.', 'S': '...', 'T': '-',
  'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-', 'Y': '-.--', 'Z': '--..',
  '1': '.----', '2': '..---', '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...', '8': '---..',
  '9': '----.', '0': '-----',
  '.': '.-.-.-', ',': '--..--', '?': '..--..', "'": '.----.', '!': '-.-.--', '/': '-..-.', '(': '-.--.',
  ')': '-.--.-', '&': '.-...', ':': '---...', ';': '-.-.-.', '=': '-...-', '+': '.-.-.', '-': '-....-',
  '_': '..--.-', '"': '.-..-.', '$': '...-..-', '@': '.--.-.'
}

// 反向映射
const reverseMorseCodeMap = Object.entries(morseCodeMap).reduce((acc, [key, value]) => {
  acc[value] = key
  return acc
}, {})

// 文本转莫尔斯
function textToMorse() {
  const text = textInput.value.toUpperCase()
  morseOutput.value = text.split('').map(char => {
    if (char === ' ') return '/'
    return morseCodeMap[char] || char
  }).join(' ')
}

// 莫尔斯转文本
function morseToText() {
  const morse = morseOutput.value
  textInput.value = morse.split(' ').map(code => {
    if (code === '/') return ' '
    return reverseMorseCodeMap[code] || code
  }).join('')
}

// 播放莫尔斯电码
function playMorseCode() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)()
  }
  
  if (isPlaying.value) return
  isPlaying.value = true
  
  const morse = morseOutput.value
  const dotDuration = 1.2 / wpm.value // 点的持续时间（秒）
  
  // 创建音频组件
  oscillator = audioContext.createOscillator()
  gainNode = audioContext.createGain()
  
  oscillator.type = 'sine'
  oscillator.frequency.value = 700 // 700Hz是标准莫尔斯电码频率
  oscillator.connect(gainNode)
  gainNode.connect(audioContext.destination)
  
  // 初始静音
  gainNode.gain.value = 0
  oscillator.start()
  
  let currentTime = audioContext.currentTime
  
  // 解析莫尔斯电码
  morse.split('').forEach(char => {
    if (char === '.') {
      // 点
      gainNode.gain.setValueAtTime(1, currentTime)
      currentTime += dotDuration
      gainNode.gain.setValueAtTime(0, currentTime)
      currentTime += dotDuration // 字符内间隔
    } else if (char === '-') {
      // 划
      gainNode.gain.setValueAtTime(1, currentTime)
      currentTime += dotDuration * 3
      gainNode.gain.setValueAtTime(0, currentTime)
      currentTime += dotDuration // 字符内间隔
    } else if (char === ' ') {
      // 字符间隔
      currentTime += dotDuration * 2 // 已经有一个点的间隔，再加两个
    } else if (char === '/') {
      // 单词间隔
      currentTime += dotDuration * 6 // 已经有一个点的间隔，再加六个
    }
  })
  
  // 播放结束后清理
  setTimeout(() => {
    stopMorseCode()
  }, (currentTime - audioContext.currentTime) * 1000)
}

// 停止播放
function stopMorseCode() {
  if (!isPlaying.value) return
  
  if (oscillator) {
    oscillator.stop()
    oscillator = null
  }
  
  isPlaying.value = false
}

// 更新WPM
function updateWPM(value) {
  wpm.value = value
}

// 组件卸载前清理
onBeforeUnmount(() => {
  if (oscillator) {
    oscillator.stop()
  }
  if (audioContext) {
    audioContext.close()
  }
})
</script>

<template>
  <div class="morse-code-converter">
    <h2>莫尔斯电码转换器</h2>
    <p>在文本和莫尔斯电码之间进行转换，并提供音频播放功能。</p>
    
    <div class="input-section">
      <label for="text-input">文本输入:</label>
      <textarea 
        id="text-input" 
        v-model="textInput" 
        rows="4" 
        placeholder="输入文本转换为莫尔斯电码..."
        @input="textToMorse"
      ></textarea>
    </div>
    
    <div class="output-section">
      <label for="morse-output">莫尔斯电码:</label>
      <textarea 
        id="morse-output" 
        v-model="morseOutput" 
        rows="4" 
        placeholder="莫尔斯电码将显示在这里..."
        @input="morseToText"
      ></textarea>
    </div>
    
    <div class="controls">
      <button 
        class="play-button" 
        @click="playMorseCode" 
        :disabled="isPlaying || !morseOutput"
      >
        <span class="button-icon">▶️</span> 播放莫尔斯电码
      </button>
      
      <button 
        class="stop-button" 
        @click="stopMorseCode" 
        :disabled="!isPlaying"
      >
        <span class="button-icon">⏹️</span> 停止播放
      </button>
    </div>
    
    <div class="speed-control">
      <label for="wpm-slider">播放速度 (WPM):</label>
      <input 
        type="range" 
        id="wpm-slider" 
        min="5" 
        max="30" 
        v-model.number="wpm" 
        @input="updateWPM($event.target.value)"
      >
      <span class="wpm-display">{{ wpm }} WPM</span>
    </div>
    
    <div class="morse-reference">
      <h4>莫尔斯电码参考表</h4>
      <div class="reference-grid">
        <div v-for="(code, char) in morseCodeMap" :key="char" class="reference-item">
          {{ char }}: {{ code }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.morse-code-converter {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 15px;
}

h2 {
  color: #333;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

p {
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
}

.input-section, .output-section {
  margin-bottom: 20px;
}

.input-section label, .output-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #444;
}

textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  font-family: monospace;
}

textarea:focus {
  outline: none;
  border-color: #3a8c3a;
  box-shadow: 0 0 0 2px rgba(58, 140, 58, 0.2);
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

button {
  background-color: #3a8c3a;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
}

button:hover {
  background-color: #2d6a2d;
}

button:disabled {
  background-color: #aaa;
  cursor: not-allowed;
}

.button-icon {
  margin-right: 8px;
}

.play-button {
  background-color: #3a8c3a;
}

.stop-button {
  background-color: #d32f2f;
}

.stop-button:hover {
  background-color: #b71c1c;
}

.speed-control {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 15px;
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 6px;
}

.speed-control label {
  margin-right: 10px;
  font-weight: 500;
  margin-bottom: 0;
}

.speed-control input {
  flex: 1;
  margin: 0 10px;
}

.wpm-display {
  font-weight: 600;
  color: #3a8c3a;
  min-width: 60px;
}

.morse-reference {
  margin-top: 30px;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.morse-reference h4 {
  color: #3a8c3a;
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.reference-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.reference-item {
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-family: monospace;
  text-align: center;
}

@media (max-width: 600px) {
  .controls {
    flex-direction: column;
  }
  
  .speed-control {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .speed-control label {
    margin-bottom: 8px;
  }
  
  .speed-control input {
    width: 100%;
    margin: 10px 0;
  }
  
  .reference-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  h2 {
    color: #e0e0e0;
  }
  
  p {
    color: #bbb;
  }
  
  .input-section label, .output-section label {
    color: #ccc;
  }
  
  textarea {
    background-color: #2c2c2c;
    border-color: #444;
    color: #e0e0e0;
  }
  
  textarea:focus {
    border-color: #7cb342;
    box-shadow: 0 0 0 2px rgba(124, 179, 66, 0.2);
  }
  
  .speed-control {
    background-color: #1e1e1e;
    border-color: #444;
  }
  
  .wpm-display {
    color: #7cb342;
  }
  
  .morse-reference {
    background-color: #1e1e1e;
    border-color: #444;
  }
  
  .morse-reference h4 {
    color: #7cb342;
    border-bottom-color: #444;
  }
  
  .reference-item {
    background-color: #2c2c2c;
    color: #e0e0e0;
  }
  
  button:hover {
    background-color: #558b2f;
  }
  
  .stop-button:hover {
    background-color: #c62828;
  }
}
</style>



